import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSubscriptionFilters, useSubscriptionsV2 } from "../services/listApi";
import { useSubscriptionMetrics } from "@/pages/subscriptions/services/subscriptionsV2Api";
import type { SubscriptionListFilters } from "../types";
import { useUsers } from "@/helpers/hooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import usePaginate from "@/components/paginate/usePaginate";
import { parseSortDirection } from "@/components/tables/types";
import { getSubscriptionColumns } from "@/pages/subscriptions/components/list/subscriptionColumns";
import { SEARCH_PARAMS, SubscriptionType } from "@/pages/subscriptions/constants";
import { useSubscriptionActions } from "./useSubscriptionActions";

export const useSubscriptionData = () => {
  const [searchParams] = useSearchParams();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();
  const sortBy = searchParams.get(SEARCH_PARAMS.SORT_BY) || "created_at";
  const sortOrder = parseSortDirection(searchParams.get(SEARCH_PARAMS.SORT), "desc");
  const { page, setPage, pageSize, updatePageSize } = usePaginate({ size: 10 });

  useEffect(() => {
    const urlPage = searchParams.get(SEARCH_PARAMS.PAGE);
    const urlPageSize = searchParams.get(SEARCH_PARAMS.PAGE_SIZE);

    if (urlPage) {
      const pageNum = parseInt(urlPage) - 1; // Convert to 0-based index
      if (pageNum >= 0 && pageNum !== page) {
        setPage(pageNum);
      }
    }

    if (urlPageSize) {
      const size = parseInt(urlPageSize);
      if (size > 0 && size !== pageSize) {
        updatePageSize(size);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filterKey = [
    searchParams.getAll(SEARCH_PARAMS.RULE_TYPE).join(),
    searchParams.getAll(SEARCH_PARAMS.CATEGORY).join(),
    searchParams.get(SEARCH_PARAMS.SEARCH),
    sortBy,
    sortOrder,
  ].join("|");

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (page !== 0) {
      setPage(0);
      updateSearchParamsByKey({ [SEARCH_PARAMS.PAGE]: "1" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const filters = useMemo((): SubscriptionListFilters => {
    const ruleTypes = searchParams.getAll(SEARCH_PARAMS.RULE_TYPE);
    const categories = searchParams.getAll(SEARCH_PARAMS.CATEGORY);
    const searchValue = searchParams.get(SEARCH_PARAMS.SEARCH);

    return {
      page: page + 1, // Backend uses 1-indexed pages
      size: pageSize,
      rule_type: ruleTypes.length > 0 ? (ruleTypes[0] as SubscriptionType) : undefined,
      category: categories.length > 0 ? categories[0] : undefined,
      search: searchValue || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
  }, [searchParams, page, pageSize, sortBy, sortOrder]);

  const {
    data: subscriptionsData,
    isLoading: subscriptionsLoading,
    isError: subscriptionsError,
  } = useSubscriptionsV2(filters);
  const { data: metricsData } = useSubscriptionMetrics();
  const { data: filtersData, isError: filtersError } = useSubscriptionFilters();
  const { getFormattedUserNameById } = useUsers();
  const { handleToggleStatus, handleDelete, handleClone } = useSubscriptionActions();

  const categoryDisplayNameLookup = useMemo(() => {
    if (!metricsData) return {};
    return metricsData.reduce(
      (acc, m) => {
        if (!acc[m.category]) acc[m.category] = m.category_display_name;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [metricsData]);

  const filterOptions = useMemo(() => {
    if (!filtersData)
      return { categories: [], subscriptionTypes: [], createdBy: [], destinations: [] };
    return {
      categories: filtersData.categories,
      subscriptionTypes: filtersData.subscription_types,
      createdBy: filtersData.created_by,
      destinations: filtersData.destinations,
    };
  }, [filtersData]);

  const subscriptionColumns = useMemo(
    () =>
      getSubscriptionColumns({
        categoryOptions: filterOptions.categories,
        createdByOptions: filterOptions.createdBy,
        destinationOptions: filterOptions.destinations,
        onToggleStatus: handleToggleStatus,
        onDelete: handleDelete,
        onClone: handleClone,
      }),
    [filterOptions, handleToggleStatus, handleDelete, handleClone]
  );

  const subscriptions = useMemo(() => subscriptionsData?.items ?? [], [subscriptionsData]);
  const numPages = subscriptionsData?.pages ?? 1;

  // Build a lookup from filter options (backend resolves user names there)
  const createdByNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const opt of filterOptions.createdBy) {
      lookup[opt.value] = opt.label;
    }
    return lookup;
  }, [filterOptions.createdBy]);

  const paginatedSubscriptions = useMemo(
    () =>
      subscriptions.map(subscription => {
        const category = subscription.category || undefined;
        const createdById = subscription.created_by;
        // Prefer backend-resolved name, then filter options lookup, then client-side user list
        const resolvedName =
          subscription.created_by_name ||
          createdByNameLookup[String(createdById)] ||
          getFormattedUserNameById(createdById) ||
          "Unknown";
        return {
          ...subscription,
          categoryDisplayName: category
            ? categoryDisplayNameLookup[category] || category
            : "Custom SQL",
          createdByName: resolvedName,
        };
      }),
    [subscriptions, categoryDisplayNameLookup, createdByNameLookup, getFormattedUserNameById]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParamsByKey({ [SEARCH_PARAMS.PAGE]: `${newPage + 1}` });
      setPage(newPage);
    },
    [updateSearchParamsByKey, setPage]
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      updateSearchParamsByKey({
        [SEARCH_PARAMS.PAGE_SIZE]: `${newSize}`,
        [SEARCH_PARAMS.PAGE]: "1",
      });
      updatePageSize(newSize);
    },
    [updateSearchParamsByKey, updatePageSize]
  );

  return {
    subscriptions,
    subscriptionsLoading,
    subscriptionsError,
    paginatedSubscriptions,
    subscriptionColumns,
    filterOptions,
    filtersError,
    page,
    pageSize,
    numPages,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    totalCount: subscriptionsData?.total ?? 0,
  };
};
